
import React, {useState} from 'react';
import TeacherLayout from "@/components/layout/teacher/teacherLayout";
import {Link, useParams} from "react-router-dom";
import {sprintf} from "@/lib/utils/sprintf";
import {URLS} from "@/lib/urls";
import {Book, Cake, Phone, TrendingDown, TrendingUp, User, X} from "lucide-react";

import {useApiSearchTeacherStudents} from "@/api/userAcademy";
import {convertToJalaliWithFormat} from "@/lib/utils/date";
import PaginationRaw from "@/components/pagination/PaginationRaw";
import SearchInputRaw from "@/components/search/SearchInputRaw";
import RenderLoading from "@/components/render-if-loading";
import {Button} from "antd";
import RenderIf from "@/components/render-if";

function TeacherStudentListPage() {
    const {id} = useParams();

    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);

    const {data: students, isPending} = useApiSearchTeacherStudents({
        lesson_id: id,
        page: page,
        per_page: perPage,
        search: search
    });

    return (
        <TeacherLayout backTo={sprintf(URLS.teacher.lessons.detail, {id: id})} pageTitle={'لیست دانش‌ آموزان کلاس'}>
            <div className="mb-2">
                <SearchInputRaw setPage={setPage} setSearch={setSearch} placeHolder={'جستجو در لیست دانش آموزان ...'}/>
            </div>

            <div className="rounded-2xl shadow-sm border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 ">
                <div className="divide-y divide-slate-100 dark:divide-gray-800">
                    <RenderLoading loading={isPending} skeleton={true}>
                        {students?.data?.data.map((student, i) => (
                            <Link
                                key={student.id + 'lesson' + id + 'index' + i}
                                to={sprintf(URLS.teacher.lessons.studentsDetail, {id: id, studentId: student.student})}
                            >
                                <div
                                    className="flex flex-wrap items-center gap-4 px-4 py-3.5 hover:bg-slate-50 dark:hover:bg-gray-800/70 cursor-pointer transition-colors"
                                >
                                    <img
                                        src={student?.student_detail?.avatar ?? '/images/profile.jpg'}
                                        alt={student?.student_detail?.code}
                                        className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-200 dark:ring-gray-700"
                                    />

                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm text-slate-800 dark:text-gray-100">
                                            {student?.student_detail?.firstname}{' '}
                                            <span className="mx-1">{student?.student_detail?.lastname}</span>

                                            <span className="text-xs">
                                                <Button variant={'link'} type={'link'}>مشاهده پروفایل</Button>
                                            </span>
                                        </p>

                                        <div className="flex flex-wrap gap-2">
                                            <span className="text-xs text-slate-500 dark:text-gray-400">
                                                {student?.student_detail?.code}
                                            </span>

                                            <span className="text-xs text-slate-500 dark:text-gray-400">
                                                فرزند {student?.student_detail?.father}
                                            </span>

                                            <span className="text-xs text-slate-500 dark:text-gray-400">
                                                متولد {convertToJalaliWithFormat(student?.student_detail?.birthdate)}
                                            </span>

                                            <span className="text-xs text-slate-500 dark:text-gray-400 flex sm:hidden">
                                                <RenderIf condition={student?.student_detail?.mobile}>
                                                    موبایل {student?.student_detail?.mobile}
                                                </RenderIf>

                                                <RenderIf condition={student?.student_detail?.parent_detail?.mobile}>
                                                    موبایل والد {student?.student_detail?.parent_detail?.mobile}
                                                </RenderIf>
                                            </span>
                                        </div>
                                    </div>

                                    <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500 dark:text-gray-400">
                                        <Phone className="w-3.5 h-3.5"/>

                                        <span>
                                            <RenderIf condition={student?.student_detail?.mobile}>
                                                {student?.student_detail?.mobile}
                                            </RenderIf>

                                            <RenderIf condition={student?.student_detail?.parent_detail?.mobile}>
                                                {student?.student_detail?.parent_detail?.mobile}
                                            </RenderIf>
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <GpaChip gpa={student.score}/>

                                        {student.absences_count > 0 && (
                                            <span
                                                className={`text-xs px-2 py-1 rounded-full ${
                                                    student.absences_count >= 4
                                                        ? 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300'
                                                        : 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300'
                                                }`}
                                            >
                                                {student.absences_count} غیبت
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </RenderLoading>
                </div>
            </div>

            <PaginationRaw data={students?.data} setPage={setPage} setPerPage={setPerPage}/>

            {selectedStudent && (
                <StudentModal student={selectedStudent} onClose={() => setSelectedStudent(null)}/>
            )}
        </TeacherLayout>
    );
}

export default TeacherStudentListPage;


function GpaChip({gpa}) {
    if (!gpa) {
        return null;
    }

    const color = gpa >= 18
        ? 'text-emerald-600 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/40'
        : gpa >= 14
            ? 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-950/40'
            : 'text-red-600 bg-red-50 dark:text-red-300 dark:bg-red-950/40';

    const Icon = gpa >= 17 ? TrendingUp : TrendingDown;

    return (
        <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${color}`}>
            <Icon className="w-3 h-3"/>
            {gpa}
        </span>
    );
}


function StudentModal({student, onClose}) {
    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/30 dark:bg-black/60 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-gray-900 border border-transparent dark:border-gray-700 rounded-2xl shadow-2xl w-full max-w-md  animate-fade-in"
                onClick={e => e.stopPropagation()}
            >
                <div className="bg-gradient-to-l from-blue-600 to-blue-500 dark:from-blue-800 dark:to-blue-700 p-6 text-white">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                            <img
                                src={student?.student_detail?.avatar ?? '/images/profile.jpg'}
                                alt={student?.student_detail?.firstname}
                                className="w-16 h-16 rounded-2xl object-cover ring-4 ring-white/30"
                            />

                            <div>
                                <h3 className="text-xl font-bold">
                                    {student?.student_detail?.firstname}{' '}
                                    {student?.student_detail?.lastname}
                                </h3>

                                <p className="text-blue-100 text-sm">
                                    {student?.student_detail?.code}
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                        >
                            <X className="w-5 h-5"/>
                        </button>
                    </div>
                </div>

                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <InfoItem
                            icon={<User className="w-4 h-4 text-slate-500 dark:text-gray-400"/>}
                            label="نام پدر"
                            value={student?.student_detail?.father}
                        />

                        <InfoItem
                            icon={<Cake className="w-4 h-4 text-pink-500 dark:text-pink-400"/>}
                            label="تاریخ تولد"
                            value={convertToJalaliWithFormat(student?.student_detail?.birthdate)}
                        />

                        <InfoItem
                            icon={<Phone className="w-4 h-4 text-blue-500 dark:text-blue-400"/>}
                            label="تلفن"
                            value={student?.student_detail?.mobile}
                        />

                        <InfoItem
                            icon={<TrendingUp className="w-4 h-4 text-emerald-500 dark:text-emerald-400"/>}
                            label="معدل"
                            value={student?.score ?? '--'}
                        />

                        <InfoItem
                            icon={<User className="w-4 h-4 text-amber-500 dark:text-amber-400"/>}
                            label="غیبت"
                            value={`${student?.absences_count ?? 0} جلسه`}
                        />

                        <InfoItem
                            icon={<Book className="w-4 h-4 text-amber-700 dark:text-amber-400"/>}
                            label="تکالیف ارسالی"
                            value={`${student?.homework_reports_count ?? 0} مورد`}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}


function InfoItem({icon, label, value}) {
    return (
        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-gray-800 rounded-xl">
            <div className="flex-shrink-0">{icon}</div>

            <div className="min-w-0">
                <p className="text-xs text-slate-500 dark:text-gray-400">{label}</p>
                <p className="text-sm font-medium text-slate-800 dark:text-gray-100 break-words">{value}</p>
            </div>
        </div>
    );
}
